// One place that builds TanStack Query cache keys, so invalidation from
// SSE handlers (and anywhere else) matches what the hooks register. Keys
// are arrays — TanStack does prefix matching from the front, so the more
// specific keys are nested under their `all` prefix on purpose:
// invalidating ["subscriber", username] re-fetches both session + radius.
// Add a builder when you add a data domain.

export const qk = {
  subscriber: {
    /** Prefix for everything about one subscriber — invalidate to refresh all of it. */
    all: (username: string) => ["subscriber", username] as const,
    /** Live PPPoE session + ONU (GET /v1/api/search?q=). */
    session: (username: string) => ["subscriber", username, "session"] as const,
    /** RADIUS DB row (GET /v1/api/radius/details). */
    radius: (username: string) => ["subscriber", username, "radius"] as const,
    /** Background live-portal re-sync (GET /v1/api/radius/live) — upserts the mirror, then refreshes `radius`. */
    radiusLive: (username: string) => ["subscriber", username, "radiusLive"] as const,
    /** Live PPPoE interface bitrate (GET /v1/api/cmd/ifrate), polled. */
    rate: (username: string, router: string) => ["subscriber", username, "rate", router] as const,
    /** Fresh over-SSH ONU detail (GET /v1/api/cmd/onu_detail). */
    onuDetail: (username: string) => ["subscriber", username, "onuDetail"] as const,
  },
  suggest: (q: string) => ["suggest", q] as const,
  fleet: {
    tiles: () => ["fleet", "tiles"] as const,
    poller: () => ["fleet", "poller"] as const,
    olts: () => ["fleet", "olts"] as const,
    oltOnus: (name: string) => ["fleet", "oltOnus", name] as const,
    oltPanel: (name: string) => ["fleet", "oltPanel", name] as const,
    oltModels: () => ["fleet", "oltModels"] as const,
    routers: () => ["fleet", "routers"] as const,
  },
  switches: {
    list: () => ["switches", "list"] as const,
    users: (name: string) => ["switches", "users", name] as const,
    /** Fresh over-SSH ONU detail for a switch box (GET /v1/api/cmd/onu_detail
     *  with the switch name hyphenated as the "username"). */
    onuDetail: (name: string) => ["switches", "onuDetail", name] as const,
  },
  boxchains: {
    /** Prefix for any box-chains query — invalidate after a mutation to refresh both. */
    all: () => ["boxchains"] as const,
    list: () => ["boxchains", "list"] as const,
    state: () => ["boxchains", "state"] as const,
  },
  oltIssues: {
    /** Prefix for any OLT-issues query — invalidate to refresh all filter views. */
    all: () => ["oltIssues"] as const,
    list: (f: { olt?: string; type?: string; q?: string }) =>
      ["oltIssues", "list", f.olt ?? "", f.type ?? "", f.q ?? ""] as const,
  },
  radiusIssues: {
    all: () => ["radiusIssues"] as const,
    list: (f: { type?: string; router?: string; olt?: string; q?: string }) =>
      ["radiusIssues", "list", f.type ?? "", f.router ?? "", f.olt ?? "", f.q ?? ""] as const,
  },
  radiusStatus: {
    state: () => ["radiusStatus", "state"] as const,
  },
  ipPhone: {
    state: () => ["ipPhone", "state"] as const,
  },
  callBlast: {
    state: () => ["callBlast", "state"] as const,
    needsFix: () => ["callBlast", "needsFix"] as const,
  },
  bulkSms: {
    /** Prefix for any bulk-SMS query — invalidate on `events:bulksms` to refresh. */
    all: () => ["bulkSms"] as const,
    state: () => ["bulkSms", "state"] as const,
    jobs: () => ["bulkSms", "jobs"] as const,
    job: (id: number) => ["bulkSms", "job", id] as const,
    campaigns: () => ["bulkSms", "campaigns"] as const,
  },
  sms: {
    main: () => ["sms", "main"] as const,
  },
  contacts: {
    /** Prefix for any contacts query — invalidate to refresh every filtered view. */
    all: () => ["contacts"] as const,
    list: (q: string) => ["contacts", "list", q] as const,
  },
  users: {
    list: () => ["users", "list"] as const,
  },
  resellers: {
    list: () => ["resellers", "list"] as const,
  },
  messenger: {
    /** Prefix for any messenger query — invalidate on `events:messenger` to refresh all. */
    all: () => ["messenger"] as const,
    state: () => ["messenger", "state"] as const,
    bubble: () => ["messenger", "bubble"] as const,
    conversation: (id: number) => ["messenger", "conversation", id] as const,
    templates: () => ["messenger", "templates"] as const,
  },
  fiber: {
    /** Prefix for any fibre query — invalidate on `events:fiberissues` to refresh all. */
    all: () => ["fiber"] as const,
    state: () => ["fiber", "state"] as const,
    capacity: () => ["fiber", "capacity"] as const,
    pons: () => ["fiber", "pons"] as const,
    tree: (olt: string, pon: number) => ["fiber", "tree", olt, pon] as const,
    distPoints: (f: { kind?: string; q?: string }) => ["fiber", "distPoints", f.kind ?? "", f.q ?? ""] as const,
    distPoint: (id: number) => ["fiber", "distPoint", id] as const,
    cables: (f: { q?: string; brand?: string }) => ["fiber", "cables", f.q ?? "", f.brand ?? ""] as const,
    cable: (id: number) => ["fiber", "cable", id] as const,
    span: (id: number) => ["fiber", "span", id] as const,
    issues: (f: { source?: string; severity?: string }) =>
      ["fiber", "issues", f.source ?? "", f.severity ?? ""] as const,
    drop: (username: string) => ["fiber", "drop", username] as const,
  },
} as const;
